import React, { useContext } from 'react';
import { StripeElementsOptionsMode } from '@stripe/stripe-js';

/**
 * True only below the `<Elements>` that StripeCoordinator mounts. Stripe's own context cannot be
 * probed without throwing, and the coordinator's SDK state is set in an effect, so it lags the
 * render in which `<Elements>` goes away (SHOP-WEB-2P). This flag is synchronous with the tree.
 */
export const StripeElementsMountedContext = React.createContext(false);

export function useStripeElementsMounted(): boolean {
  return useContext(StripeElementsMountedContext);
}

/**
 * The Stripe Elements checkout's customer session, as the `<Elements>` around it stand.
 */
export interface StripeElementsSession {
  /**
   * The Elements wait for a logged-in customer's CustomerSession, which they read at creation only:
   * nothing to draw yet, and nothing wrong. Not mounted, but not the "no Stripe" case either.
   */
  pending: boolean;
  /** The Elements were created with a CustomerSession (Stripe's own save box). */
  withCustomerSession: boolean;
  /** They could not load with it: carry on without it, as a guest's Elements. The box is optional. */
  dropCustomerSession: () => void;
  /**
   * The payment block still shows its placeholder, the real one laid out unseen beneath it. The Pay
   * button waits for it: a selection paid from there would be one the customer never saw, and a
   * card row's field errors would land in an invisible iframe.
   */
  blockPending: boolean;
  setBlockPending: (pending: boolean) => void;
  /** What the Elements are sized to, in the currency's minor units: the amount a wallet sheet shows. */
  amount: number;
  /**
   * An Express Checkout wallet sheet is open or its confirm is in flight. The Elements must not be
   * re-created meanwhile (a refreshed CustomerSession would): the sheet's confirm would reach an
   * element without a handler (SHOP-WEB-45).
   */
  setWalletSheetOpen: (open: boolean) => void;
  /** Whether Stripe.js itself came: a script blocked or cut off never draws an Element. */
  stripeJs: StripeJsState;
  /**
   * The Elements are re-created, Stripe.js asked for again if it failed (SHOP-WEB-47): a session that
   * hung or a script the network dropped gets a fresh start. Ignored while a wallet sheet is open or a
   * payment is in flight; says whether it went ahead.
   */
  retryElements: (reason: 'auto' | 'manual') => boolean;
  /** How many times the Elements have been re-created on this page. */
  elementsAttempt: number;
  /** The options the Elements were created with, for the Express Checkout Element's own group. */
  elementsOptions: null | StripeElementsOptionsMode;
}

export type StripeJsState = 'loading' | 'loaded' | 'failed';

export const StripeElementsSessionContext = React.createContext<StripeElementsSession>({
  pending: false,
  withCustomerSession: false,
  dropCustomerSession: () => undefined,
  blockPending: false,
  setBlockPending: () => undefined,
  amount: 0,
  setWalletSheetOpen: () => undefined,
  stripeJs: 'loading',
  retryElements: () => false,
  elementsAttempt: 0,
  elementsOptions: null,
});

export function useStripeElementsSession(): StripeElementsSession {
  return useContext(StripeElementsSessionContext);
}
